import { decimalToCents } from "@/features/staff/reports/charts/series"

/**
 * Segment widths for a proportion bar, as percentages of the whole.
 *
 * These are CSS widths and nothing else. The rule this screen runs on is that no
 * peso figure is re-derived in the browser; a derived number may become
 * geometry, never text. The labelled row list under the bar carries the exact
 * server strings, so the bar never has to.
 *
 * A segment with anything in it gets at least a sliver, for the same reason
 * `barPercent` does: a rail that took one payment all period should read as "a
 * little", not as "not used". Widths are then trimmed back so the row never
 * overflows its track — the largest segment absorbs the rounding, which is the
 * one place a pixel can go missing without anybody being misled.
 */
export function proportionPercents(amounts: string[]): number[] {
  const cents = amounts.map((amount) => Math.max(0, decimalToCents(amount)))
  const total = cents.reduce((sum, value) => sum + value, 0)

  if (total <= 0) return cents.map(() => 0)

  const raw = cents.map((value) =>
    value <= 0 ? 0 : Math.max(2, Math.round((value / total) * 1000) / 10)
  )

  const overflow = raw.reduce((sum, value) => sum + value, 0) - 100
  if (overflow <= 0) return raw

  const widest = raw.reduce(
    (best, value, index) => (value > raw[best] ? index : best),
    0
  )
  const trimmed = [...raw]
  trimmed[widest] = Math.max(2, Math.round((trimmed[widest] - overflow) * 10) / 10)

  return trimmed
}

/**
 * The ordinal ink a segment and its row marker share.
 *
 * **Not a colour key.** Every segment is direct-labelled by the row directly
 * under it, in the same order; the ink is redundant encoding that lets the eye
 * follow a slab down to its label, and the bar is still readable in grayscale
 * because the `gap-px` hairline — the card showing through — is what separates
 * one segment from the next.
 *
 * One ink, stepped by opacity, so the ramp survives the theme flip with no
 * second declaration and no `.dark` override. Measured against the `--muted`
 * track the segments sit on, which is also what shows through the `gap-px`
 * hairline: 18.15:1 / 7.33:1 / 3.11:1 in light, 14.48:1 / 7.82:1 / 4.12:1 in
 * dark. Every step clears the 3:1 WCAG AA floor for a non-text graphic in both
 * themes.
 *
 * That floor is exactly why the ramp is NOT the three neutral tokens the rest of
 * the charts use. `--muted` as a third categorical fill measures 1.09:1 against
 * `--card` in light — the segment would be invisible, not subtle.
 *
 * Three steps covers the three payment rails the product has. It cycles rather
 * than running out, so a fourth rail degrades to a repeat instead of to a blank.
 */
const PROPORTION_INKS = ["bg-foreground", "bg-foreground/70", "bg-foreground/45"] as const

export function proportionInk(index: number): string {
  return PROPORTION_INKS[index % PROPORTION_INKS.length]
}
